/**
 * ============================================================================
 * Iron Meridian - API Service
 * ============================================================================
 * 
 * Centralized API calls to the game backend.
 * Uses Cloudflare Pages Functions for serverless API.
 */

const API_BASE = '/api'

// Helper for API calls
async function apiCall(endpoint, options = {}) {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    })
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }
    
    return response.json()
  } catch (error) {
    console.error('API call failed:', error.message)
    throw error
  }
}

/**
 * Get current game state
 */
export async function getGameState() {
  return apiCall('/game/state')
}

/**
 * Start a new game
 */
export async function startNewGame(playerCountry = 'USA') {
  return apiCall('/game/new', {
    method: 'POST',
    body: JSON.stringify({ playerCountry })
  })
}

/**
 * Submit turn actions
 */
export async function submitTurn(actions) {
  return apiCall('/game/turn', {
    method: 'POST',
    body: JSON.stringify({ actions })
  })
}

/**
 * Get all countries
 */
export async function getCountries() {
  return apiCall('/countries')
}

/**
 * Get specific country details
 */
export async function getCountry(countryId) {
  return apiCall(`/countries/${countryId}`)
}

/**
 * Resolve combat
 */
export async function resolveCombat(attacker, defender) {
  return apiCall('/combat/resolve', {
    method: 'POST',
    body: JSON.stringify({ attacker, defender })
  })
}

// ============================================================================
// DIPLOMACY API
// ============================================================================

/**
 * Get diplomatic relationships for the current player
 */
export async function getDiplomacyState() {
  return apiCall('/diplomacy/state')
}

/**
 * Propose a treaty to another country (client-side for singleplayer)
 * In singleplayer, treaties are immediately accepted based on trust level
 * @param {string} targetCountryId - ISO3 code of target country
 * @param {string} treatyType - Type of treaty (ALLIANCE, NAP, TRADE)
 * @param {number|null} duration - Duration in turns (null = permanent)
 * @returns {Object} Result with success status and updated diplomacy
 */
export function proposeTreatyLocal(targetCountryId, treatyType, duration = null) {
  const state = loadDiplomacyState()
  const entry = state.relationships[targetCountryId]
  
  if (!entry) {
    return { success: false, error: `Country ${targetCountryId} not found` }
  }
  
  // Check trust requirements for treaty acceptance
  const trustRequirements = {
    NAP: 0,
    TRADE: 40,
    ALLIANCE: 60
  }
  
  const requiredTrust = trustRequirements[treatyType] ?? 0
  
  // AI decision based on trust + some randomness
  const acceptanceChance = Math.min(1, Math.max(0, (entry.trust - requiredTrust + 50) / 100))
  const accepted = Math.random() < acceptanceChance
  
  if (!accepted) {
    // Rejected - small trust penalty
    entry.trust = Math.max(-100, entry.trust - 5)
    state.logs.push({
      turn: state.currentTurn,
      type: 'TREATY_REJECTED',
      countryId: targetCountryId,
      countryName: entry.countryName,
      treatyType,
      message: `${entry.countryName} rejected your ${treatyType} proposal`
    })
    saveDiplomacyState(state)
    return { success: false, rejected: true, message: `${entry.countryName} rejected the proposal` }
  }
  
  // Accepted - add treaty and boost trust
  const treaty = {
    id: `${treatyType}-${targetCountryId}-${state.currentTurn}`,
    type: treatyType,
    startedTurn: state.currentTurn,
    expiresTurn: duration ? state.currentTurn + duration : undefined
  }
  
  entry.treaties = entry.treaties || []
  entry.treaties.push(treaty)
  entry.trust = Math.min(100, entry.trust + 15)
  entry.lastInteractionTurn = state.currentTurn
  
  // Update stance if trust improved enough
  const newStance = deriveStanceFromTrust(entry.trust)
  if (newStance !== entry.stance) {
    entry.stance = newStance
  }
  
  state.logs.push({
    turn: state.currentTurn,
    type: 'TREATY_SIGNED',
    countryId: targetCountryId,
    countryName: entry.countryName,
    treatyType,
    message: `${entry.countryName} accepted your ${treatyType}!`
  })
  
  saveDiplomacyState(state)
  return { success: true, treaty, message: `${entry.countryName} accepted the ${treatyType}!` }
}

/**
 * Cancel/break a treaty with a country (client-side)
 * @param {string} targetCountryId - ISO3 code
 * @param {string} treatyType - Type of treaty to cancel
 * @returns {Object} Result
 */
export function cancelTreatyLocal(targetCountryId, treatyType) {
  const state = loadDiplomacyState()
  const entry = state.relationships[targetCountryId]
  
  if (!entry) {
    return { success: false, error: `Country ${targetCountryId} not found` }
  }
  
  // Find and remove the treaty
  const treatyIndex = entry.treaties?.findIndex(t => t.type === treatyType)
  if (treatyIndex === -1 || treatyIndex === undefined) {
    return { success: false, error: 'No such treaty exists' }
  }
  
  entry.treaties.splice(treatyIndex, 1)
  
  // Major trust penalty for breaking treaties
  const trustPenalty = {
    ALLIANCE: -40,
    NAP: -25,
    TRADE: -15
  }
  
  entry.trust = Math.max(-100, entry.trust + (trustPenalty[treatyType] || -20))
  entry.lastInteractionTurn = state.currentTurn
  
  // Update stance
  entry.stance = deriveStanceFromTrust(entry.trust)
  
  state.logs.push({
    turn: state.currentTurn,
    type: 'TREATY_BROKEN',
    countryId: targetCountryId,
    countryName: entry.countryName,
    treatyType,
    message: `You broke the ${treatyType} with ${entry.countryName}!`
  })
  
  saveDiplomacyState(state)
  return { success: true, message: `Treaty cancelled. Trust with ${entry.countryName} decreased.` }
}

/**
 * Propose a treaty - uses local handling for singleplayer
 */
export async function proposeTreaty(targetPlayerId, treaty, duration = null, terms = {}) {
  // For singleplayer, use local handling
  const result = proposeTreatyLocal(targetPlayerId, treaty, duration)
  return { success: result.success, data: result }
}

/**
 * Accept a pending treaty proposal
 */
export async function acceptProposal(proposerPlayerId, treaty, duration = null, terms = {}) {
  return apiCall('/diplomacy/accept', {
    method: 'POST',
    body: JSON.stringify({
      proposer_player_id: proposerPlayerId,
      treaty,
      duration,
      terms
    })
  })
}

/**
 * Reject a pending treaty proposal
 */
export async function rejectProposal(proposerPlayerId, treaty) {
  return apiCall('/diplomacy/reject', {
    method: 'POST',
    body: JSON.stringify({
      proposer_player_id: proposerPlayerId,
      treaty
    })
  })
}

/**
 * Cancel an existing treaty - uses local handling for singleplayer
 */
export async function cancelTreaty(targetPlayerId, treatyType) {
  const result = cancelTreatyLocal(targetPlayerId, treatyType)
  return { success: result.success, data: result }
}

/**
 * Get pending proposals for the current player
 */
export async function getPendingProposals() {
  return apiCall('/diplomacy/proposals')
}

// ============================================================================
// TECH RESEARCH API (Client-side state with localStorage for singleplayer)
// ============================================================================

// Technology definitions for client-side validation
const TECHNOLOGIES = {
  basic_training: { id: 'basic_training', name: 'Basic Training', branch: 'military', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  fortifications: { id: 'fortifications', name: 'Fortifications', branch: 'military', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  professional_army: { id: 'professional_army', name: 'Professional Army', branch: 'military', tier: 2, cost: 200, prerequisites: ['basic_training'], exclusions: ['citizen_militia'] },
  citizen_militia: { id: 'citizen_militia', name: 'Citizen Militia', branch: 'military', tier: 2, cost: 150, prerequisites: ['basic_training'], exclusions: ['professional_army'] },
  siege_warfare: { id: 'siege_warfare', name: 'Siege Warfare', branch: 'military', tier: 2, cost: 180, prerequisites: ['fortifications'], exclusions: [] },
  agriculture: { id: 'agriculture', name: 'Agriculture', branch: 'economy', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  basic_industry: { id: 'basic_industry', name: 'Basic Industry', branch: 'economy', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  trade_networks: { id: 'trade_networks', name: 'Trade Networks', branch: 'economy', tier: 2, cost: 200, prerequisites: ['agriculture'], exclusions: ['self_sufficiency'] },
  self_sufficiency: { id: 'self_sufficiency', name: 'Self-Sufficiency', branch: 'economy', tier: 2, cost: 180, prerequisites: ['agriculture'], exclusions: ['trade_networks'] },
  civil_service: { id: 'civil_service', name: 'Civil Service', branch: 'governance', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  public_order: { id: 'public_order', name: 'Public Order', branch: 'governance', tier: 1, cost: 100, prerequisites: [], exclusions: [] },
  constitutional_law: { id: 'constitutional_law', name: 'Constitutional Law', branch: 'governance', tier: 2, cost: 200, prerequisites: ['civil_service'], exclusions: ['centralized_authority'] },
  centralized_authority: { id: 'centralized_authority', name: 'Centralized Authority', branch: 'governance', tier: 2, cost: 180, prerequisites: ['civil_service'], exclusions: ['constitutional_law'] }
}

const TECH_STATE_KEY = 'ironMeridian_techState'

function getDefaultTechState() {
  return {
    researched: [],
    currentResearch: null,
    researchPoints: 50,
    researchHistory: []
  }
}

function loadTechState() {
  try {
    const stored = localStorage.getItem(TECH_STATE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to load tech state from localStorage:', e)
  }
  return getDefaultTechState()
}

function saveTechState(state) {
  try {
    localStorage.setItem(TECH_STATE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save tech state to localStorage:', e)
  }
}

function canResearchTech(techId, researched) {
  const tech = TECHNOLOGIES[techId]
  if (!tech) return { can: false, reason: 'Technology not found' }
  
  if (researched.includes(techId)) {
    return { can: false, reason: 'Already researched' }
  }
  
  for (const prereq of tech.prerequisites) {
    if (!researched.includes(prereq)) {
      return { can: false, reason: `Requires ${TECHNOLOGIES[prereq]?.name || prereq}` }
    }
  }
  
  for (const excl of tech.exclusions) {
    if (researched.includes(excl)) {
      return { can: false, reason: `Blocked by ${TECHNOLOGIES[excl]?.name || excl}` }
    }
  }
  
  return { can: true, reason: null }
}

function buildTechTree(state) {
  const techs = []
  for (const tech of Object.values(TECHNOLOGIES)) {
    const isResearched = state.researched.includes(tech.id)
    const isCurrentlyResearching = state.currentResearch?.techId === tech.id
    const canResult = canResearchTech(tech.id, state.researched)
    
    let status = 'locked'
    if (isResearched) {
      status = 'completed'
    } else if (isCurrentlyResearching) {
      status = 'researching'
    } else if (canResult.can) {
      status = 'available'
    } else if (tech.exclusions.some(excl => state.researched.includes(excl))) {
      status = 'blocked'
    }
    
    techs.push({
      ...tech,
      status,
      isResearched,
      isCurrentlyResearching,
      canResearch: canResult.can,
      lockReason: canResult.reason,
      progress: isCurrentlyResearching ? state.currentResearch.pointsAccumulated : 0,
      progressPercent: isCurrentlyResearching 
        ? Math.floor((state.currentResearch.pointsAccumulated / tech.cost) * 100)
        : (isResearched ? 100 : 0)
    })
  }
  return techs
}

/**
 * Get current research state (from localStorage for singleplayer)
 */
export async function getTechState() {
  const state = loadTechState()
  const techTree = buildTechTree(state)
  
  return {
    success: true,
    data: {
      researched: state.researched,
      currentResearch: state.currentResearch,
      researchPoints: state.researchPoints,
      researchHistory: state.researchHistory,
      techTree: techTree
    }
  }
}

/**
 * Start researching a technology
 * @param {string} techId - ID of the technology to research
 */
export async function startResearch(techId) {
  const state = loadTechState()
  const tech = TECHNOLOGIES[techId]
  
  if (!tech) {
    return { success: false, error: 'Technology not found' }
  }
  
  // Check if can research
  const canResult = canResearchTech(techId, state.researched)
  if (!canResult.can) {
    return { success: false, error: canResult.reason }
  }
  
  // Check if already researching something
  if (state.currentResearch) {
    return { 
      success: false, 
      error: `Already researching ${TECHNOLOGIES[state.currentResearch.techId]?.name}. Cancel it first.` 
    }
  }
  
  // Start the research
  state.currentResearch = {
    techId,
    startedAt: new Date().toISOString(),
    pointsAccumulated: 0
  }
  
  saveTechState(state)
  
  return {
    success: true,
    data: {
      message: `Started researching ${tech.name}`,
      tech: tech,
      currentResearch: state.currentResearch,
      researchState: state
    }
  }
}

/**
 * Cancel current research (refunds 50% of accumulated points)
 */
export async function cancelResearch() {
  const state = loadTechState()
  
  if (!state.currentResearch) {
    return { success: false, error: 'No active research to cancel' }
  }
  
  const techId = state.currentResearch.techId
  const tech = TECHNOLOGIES[techId]
  const pointsAccumulated = state.currentResearch.pointsAccumulated || 0
  
  // Refund 50% of accumulated points
  const refund = Math.floor(pointsAccumulated * 0.5)
  state.researchPoints += refund
  state.currentResearch = null
  
  saveTechState(state)
  
  return {
    success: true,
    data: {
      message: `Cancelled ${tech?.name || techId} research. ${refund} points refunded.`,
      refund,
      researchState: state
    }
  }
}

/**
 * Reset tech state (for new game)
 */
export function resetTechState() {
  saveTechState(getDefaultTechState())
}

/**
 * Progress research by adding points (called each turn)
 * @param {number} pointsToAdd - Research points generated this turn
 * @returns {object} - Result with any completed research
 */
export function progressResearch(pointsToAdd = 10) {
  const state = loadTechState()
  
  if (!state.currentResearch) {
    return { success: true, completed: null, progress: null }
  }
  
  const techId = state.currentResearch.techId
  const tech = TECHNOLOGIES[techId]
  if (!tech) {
    return { success: false, error: 'Invalid tech in progress' }
  }
  
  // Add points to current research
  state.currentResearch.pointsAccumulated += pointsToAdd
  
  // Check if research is complete
  if (state.currentResearch.pointsAccumulated >= tech.cost) {
    // Complete the research
    state.researched.push(techId)
    state.researchHistory.push({
      techId,
      completedAt: new Date().toISOString(),
      startedAt: state.currentResearch.startedAt
    })
    
    const completedResearch = { ...state.currentResearch, tech }
    state.currentResearch = null
    
    saveTechState(state)
    
    return {
      success: true,
      completed: completedResearch,
      message: `Research complete: ${tech.name}!`
    }
  }
  
  saveTechState(state)
  
  return {
    success: true,
    completed: null,
    progress: {
      techId,
      techName: tech.name,
      current: state.currentResearch.pointsAccumulated,
      required: tech.cost,
      percentage: Math.floor((state.currentResearch.pointsAccumulated / tech.cost) * 100)
    }
  }
}

/**
 * Clear all site data (localStorage)
 */
export function clearAllSiteData() {
  try {
    localStorage.clear()
    return { success: true, message: 'All site data cleared' }
  } catch (e) {
    console.error('Failed to clear site data:', e)
    return { success: false, error: e.message }
  }
}

// ============================================================================
// DIPLOMACY STATE (Client-side localStorage for singleplayer)
// Uses diplomacyEngine.js for all logic
// ============================================================================

import {
  generateInitialDiplomacyState,
  processDiplomacyTurn,
  modifyTrust,
  addTreaty,
  removeTreaty,
  filterAndSortDiplomacy,
  validateDiplomacyState,
  getTrustStats,
  getCountriesByStance,
  deriveStanceFromTrust,
  DIPLOMATIC_STANCES,
  TREATY_TYPES,
  TRUST_MODIFIERS
} from './diplomacyEngine.js'

const DIPLOMACY_STATE_KEY = 'ironMeridian_diplomacyState'

/**
 * @typedef {Object} DiplomacyEntry
 * @property {string} countryId - ISO-3166-1 alpha-3 code
 * @property {string} countryName - Display name
 * @property {number} trust - Trust score from -100 to +100
 * @property {string} stance - Current diplomatic stance (ALLY, FRIENDLY, NEUTRAL, WARY, HOSTILE)
 * @property {string} previousStance - Previous stance (for transition tracking)
 * @property {Array} treaties - Active treaties [{id, type, startedTurn, expiresTurn?}]
 * @property {Array} trustHistory - Recent trust changes [{turn, delta, reason}]
 */

/**
 * @typedef {Object} DiplomacyState
 * @property {string} playerCountryId - Player's country ISO code
 * @property {number} gameSeed - Seed used for deterministic generation
 * @property {number} currentTurn - Current game turn
 * @property {Object.<string, DiplomacyEntry>} relationships - Map of countryId to DiplomacyEntry
 * @property {Array} pendingProposals - Incoming treaty proposals
 * @property {Array} logs - Diplomacy event logs for turn summary
 */

function getDefaultDiplomacyState() {
  return {
    playerCountryId: null,
    gameSeed: Date.now(),
    currentTurn: 1,
    relationships: {},
    pendingProposals: [],
    logs: []
  }
}

/**
 * Load diplomacy state from localStorage
 * @returns {DiplomacyState}
 */
export function loadDiplomacyState() {
  try {
    const stored = localStorage.getItem(DIPLOMACY_STATE_KEY)
    if (stored) {
      const state = JSON.parse(stored)
      // Validate the loaded state
      if (state.relationships && typeof state.relationships === 'object') {
        // Migrate old entries that might be missing required fields
        for (const [iso3, entry] of Object.entries(state.relationships)) {
          if (!entry.treaties) entry.treaties = []
          if (!entry.iso3) entry.iso3 = iso3
          // Ensure countryName fallback to iso3 if missing
          if (!entry.countryName && !entry.name) entry.countryName = iso3
          if (entry.trust === undefined) entry.trust = 0
          if (!entry.stance) entry.stance = 'NEUTRAL'
          if (!entry.lastInteractionTurn) entry.lastInteractionTurn = 0
        }
        return state
      }
    }
  } catch (e) {
    console.warn('Failed to load diplomacy state from localStorage:', e)
  }
  return getDefaultDiplomacyState()
}

/**
 * Save diplomacy state to localStorage
 * @param {DiplomacyState} state
 */
export function saveDiplomacyState(state) {
  try {
    localStorage.setItem(DIPLOMACY_STATE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save diplomacy state to localStorage:', e)
  }
}

/**
 * Generate diplomacy relationships from countries object (client-side)
 * Uses seeded RNG for deterministic generation
 * @param {Object} countries - Map of countryId to country data
 * @param {string} playerCountryId - Player's country ISO code
 * @param {number} [gameSeed] - Optional seed for RNG (defaults to Date.now())
 * @returns {DiplomacyState}
 */
export function generateDiplomacyFromCountries(countries, playerCountryId, gameSeed = null) {
  const seed = gameSeed ?? Date.now()
  
  // Build country map for initial state generation (excluding player country)
  const countryMap = {}
  for (const [countryId, country] of Object.entries(countries)) {
    if (countryId === playerCountryId) continue
    countryMap[countryId] = {
      name: country.name || countryId,
      controller: country.controller || 'neutral',
      personality: country.personality || null,
      military: country.military || 0,
      economy: country.economy || 0
    }
  }
  
  // Generate deterministic initial relationships
  const relationships = generateInitialDiplomacyState({ 
    countries: countryMap, 
    seed, 
    playerCountry: playerCountryId 
  })
  
  const diplomacyState = {
    playerCountryId,
    gameSeed: seed,
    currentTurn: 1,
    relationships,
    pendingProposals: [],
    logs: []
  }
  
  // Validate and save (pass relationships for self-validation, skip country check)
  const validation = validateDiplomacyState(relationships, countryMap, playerCountryId)
  if (!validation.valid) {
    console.warn('Generated diplomacy state has issues:', validation.errors)
  }
  
  saveDiplomacyState(diplomacyState)
  return diplomacyState
}

/**
 * Initialize diplomacy from server data (if provided)
 * Falls back to generating client-side if server data is empty
 * @param {Object} diplomacyData - Server-provided diplomacy data
 * @param {Object} countries - Countries map for fallback generation
 * @param {string} playerCountryId - Player's country ID
 * @returns {DiplomacyState}
 */
export function initializeDiplomacyFromNewGame(diplomacyData, countries = null, playerCountryId = null) {
  // Check if server provided valid diplomacy data
  if (diplomacyData && diplomacyData.relationships && Object.keys(diplomacyData.relationships).length > 0) {
    saveDiplomacyState(diplomacyData)
    return diplomacyData
  }
  
  // Generate client-side if we have countries
  if (countries && playerCountryId) {
    return generateDiplomacyFromCountries(countries, playerCountryId)
  }
  
  return getDefaultDiplomacyState()
}

/**
 * Apply trust modification to a specific country
 * @param {string} countryId - Target country ISO code
 * @param {number} delta - Trust change amount
 * @param {string} reason - Reason for the change
 * @param {number} [currentTurn=1] - Current game turn
 * @returns {Object} Result with updated entry and logs
 */
export function applyTrustChange(countryId, delta, reason, currentTurn = 1) {
  const state = loadDiplomacyState()
  
  if (!state.relationships[countryId]) {
    return { success: false, error: `Country ${countryId} not found in diplomacy state` }
  }
  
  const result = modifyTrust(state.relationships[countryId], delta, reason, currentTurn)
  state.relationships[countryId] = result.entry
  
  // Add to logs if there was a stance change
  if (result.log.stanceChanged) {
    state.logs.push({
      turn: currentTurn,
      type: 'STANCE_CHANGE',
      countryId,
      countryName: result.entry.countryName,
      from: result.log.previousStance,
      to: result.log.newStance,
      trust: result.entry.trust
    })
  }
  
  saveDiplomacyState(state)
  
  return {
    success: true,
    entry: result.entry,
    log: result.log
  }
}

/**
 * Process end-of-turn diplomacy updates
 * @param {number} currentTurn - Current turn number
 * @returns {Object} Result with all logs and changes
 */
export function processDiplomacyEndOfTurn(currentTurn) {
  const state = loadDiplomacyState()
  const result = processDiplomacyTurn(state.relationships, currentTurn)
  
  // Update relationships with processed state
  state.relationships = result.diplomacy
  
  // Merge new events as logs
  const logs = result.events.map(event => ({
    turn: currentTurn,
    type: event.type,
    countryId: event.iso3,
    countryName: event.countryName,
    ...event
  }))
  state.logs = [...(state.logs || []), ...logs]
  state.currentTurn = currentTurn
  
  saveDiplomacyState(state)
  
  return { logs, events: result.events }
}

/**
 * Get filtered and sorted diplomacy list for UI
 * @param {Object} options - Filter and sort options
 * @returns {Array<DiplomacyEntry>}
 */
export function getDiplomacyList(options = {}) {
  const state = loadDiplomacyState()
  return filterAndSortDiplomacy(state.relationships, options)
}

/**
 * Get diplomacy statistics
 * @returns {Object} Stats including counts by stance, average trust
 */
export function getDiplomacyStats() {
  const state = loadDiplomacyState()
  return getTrustStats(state.relationships)
}

/**
 * Get countries grouped by stance
 * @returns {Object} Map of stance to country arrays
 */
export function getCountriesGroupedByStance() {
  const state = loadDiplomacyState()
  return getCountriesByStance(state.relationships)
}

/**
 * Clear diplomacy logs (after showing turn summary)
 */
export function clearDiplomacyLogs() {
  const state = loadDiplomacyState()
  state.logs = []
  saveDiplomacyState(state)
}

// Re-export engine constants for use in components
export { DIPLOMATIC_STANCES, TREATY_TYPES, TRUST_MODIFIERS }

// Check if we're in demo mode (legacy - always false with Pages Functions)
export function isDemoMode() {
  return false
}
